import InputMask from 'react-input-mask'
import { Controller, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { FormGroup } from '../../../../../../../components/FormElements/FormGroup'
import { SelectWithSearch } from '../../../../../../../components/SelectWithSearch'
import { Input } from '../../../../../../../components/FormElements/Input'
import { Button } from '../../../../../../../components/Button'
import { useBankStepForm } from './useBankStepForm'

type BankStepFormProps = {
  isSubmitting: boolean
  onSubmit: (data: any) => void
  onCancel: () => void
}

export const BankStepForm = ({
  isSubmitting,
  onSubmit,
  onCancel,
}: BankStepFormProps) => {
  const { formatMessage } = useIntl()
  const { control, handleSubmit } = useFormContext()
  const {
    brazilianBankOptions,
    handleBeforeMaskedBankBranchChangeStates,
    handleBeforeMaskedCurrentAccountChangeStates,
  } = useBankStepForm()

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="max-w-[415px]">
      <div className="mb-4">
        <Controller
          name="bank"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <FormGroup
              label={formatMessage({ id: 'BANK' })}
              error={error?.message && formatMessage({ id: error.message })}
            >
              <SelectWithSearch
                searchPlaceholder={formatMessage({ id: 'SEARCH_BY_BANK' })}
                value={value}
                onValueChange={onChange}
                defaultValue={value}
                options={brazilianBankOptions}
                noButton
              />
            </FormGroup>
          )}
        />
      </div>

      <div className="flex items-baseline gap-4 mb-10">
        <div className="grow">
          <Controller
            name="bankBranch"
            control={control}
            render={({
              field: { value = '', onChange },
              fieldState: { error, invalid, isDirty },
            }) => {
              return (
                <InputMask
                  value={value}
                  onChange={onChange}
                  mask="9999-9"
                  maskPlaceholder={null}
                  beforeMaskedStateChange={
                    handleBeforeMaskedBankBranchChangeStates
                  }
                >
                  <Input
                    label={formatMessage({ id: 'BANK_BRANCH' })}
                    placeholder="0000"
                    isValid={!invalid && isDirty}
                    errorMessage={
                      error?.message && formatMessage({ id: error.message })
                    }
                  />
                </InputMask>
              )
            }}
          />
        </div>

        <div className="grow">
          <Controller
            name="currentAccount"
            control={control}
            render={({
              field: { value = '', onChange },
              fieldState: { error, invalid, isDirty },
            }) => (
              <InputMask
                mask="999999999-9"
                maskPlaceholder={null}
                value={value}
                onChange={onChange}
                beforeMaskedStateChange={
                  handleBeforeMaskedCurrentAccountChangeStates
                }
              >
                <Input
                  label={formatMessage({ id: 'CURRENT_ACCOUNT' })}
                  placeholder="00000-0"
                  isValid={!invalid && isDirty}
                  errorMessage={
                    error?.message && formatMessage({ id: error.message })
                  }
                />
              </InputMask>
            )}
          />
        </div>
      </div>

      <div className="md:max-w-[284px] flex gap-2">
        <Button type="button" variant="outline" onClick={onCancel} fullWidth>
          {formatMessage({ id: 'CANCEL' })}
        </Button>

        <Button type="submit" isLoading={isSubmitting} fullWidth>
          {formatMessage({ id: 'SAVE' })}
        </Button>
      </div>
    </form>
  )
}
